<template>
  <Radar
    v-if="internalData?.datasets?.length"
    :data="internalData"
    :options="internalOptions"
    class="baseChartRadar"
    :class="{
      isMutable,
    }"
  />
  <!-- <BaseChartServerRadar
    :chart-data="internalData"
    :options="internalOptions"
  /> -->
</template>

<script>
import { insertIf } from '@js/components/resultsRenderHelpers';
import { Chart as ChartJS, Filler, LineElement, PointElement, RadialLinearScale, Tooltip } from 'chart.js';
import { Radar } from 'vue-chartjs';

let element, yAxisID, xAxisID, rAxisID, type, stacked, floatingBar, initValue, curDatasetIndex, curIndex, eventSettings;
let isDragging = false;

function getElement (event, chartInstance, callback) {
	type = chartInstance.config.type;

	if (!isClickInsideChart(event, chartInstance)) {
		element = null;
		return;
	}

	element = determineElement(event, chartInstance);

	const { draggableIndexes } = chartInstance.config.options.plugins.dragData;

	// Find the closest allowed dataset index
	if (draggableIndexes.length && !draggableIndexes.includes(element.datasetIndex)) {
		element.datasetIndex = draggableIndexes.findIndex(index => index !== element.datasetIndex);
	}

	if (element) {
		const datasetIndex = element.datasetIndex;

		const index = element.index;

		openToolTip(chartInstance, index);

		// save element settings
		eventSettings = getSafe(() => chartInstance.config.options.plugins.tooltip.animation);

		const dataset = chartInstance.data.datasets[datasetIndex];
		const datasetMeta = chartInstance.getDatasetMeta(datasetIndex);
		const curValue = dataset.data[index];
		// get the id of the datasets scale
		xAxisID = datasetMeta.xAxisID;
		yAxisID = datasetMeta.yAxisID;
		rAxisID = datasetMeta.rAxisID;

		// check if dragging the dataset or datapoint is prohibited
		if (dataset.dragData === false
      || (chartInstance.config.options.scales[xAxisID] && chartInstance.config.options.scales[xAxisID].dragData === false)
      || (chartInstance.config.options.scales[yAxisID] && chartInstance.config.options.scales[yAxisID].dragData === false)
      || (chartInstance.config.options.scales[rAxisID] && chartInstance.config.options.scales[rAxisID].rAxisID === false)
      || dataset.data[element.index].dragData === false
		) {
			element = null;
			return;
		}

		if (type === 'bar') {
			stacked = chartInstance.config.options.scales[xAxisID].stacked;

			// if a bar has a data point that is an array of length 2, it's a floating bar
			const samplePoint = chartInstance.data.datasets[0].data[0];
			floatingBar = (samplePoint !== null) && Array.isArray(samplePoint) && samplePoint.length == 2;

			const data = {};
			const newPos = calcPosition(event, chartInstance, datasetIndex, index, data);
			initValue = newPos - curValue;
		}

		// disable the tooltip animation
		if (chartInstance.config.options.plugins.dragData.showTooltip === undefined || chartInstance.config.options.plugins.dragData.showTooltip) {
			if (!chartInstance.config.options.plugins.tooltip) {
				chartInstance.config.options.plugins.tooltip = {};
			}
			chartInstance.config.options.plugins.tooltip.animation = false;
		}

		if (typeof callback === 'function' && element) {
			const value = type === 'radar' ? calcRadar(event, chartInstance, element) : calcPosition(event, chartInstance, datasetIndex, index);
			if (callback(event, datasetIndex, index, value) === false) {
				element = null;
			}
		}

		const pluginOptions = chartInstance.config.options.plugins.dragData;

		updateData(event, chartInstance, pluginOptions, pluginOptions.onDrag);
	}
}

function updateData (event, chartInstance, pluginOptions, callback) {
	if (element) {
		noEventDefault(event);
		curDatasetIndex = element.datasetIndex;
		curIndex = element.index;

		isDragging = true;

		let dataPoint = chartInstance.data.datasets[curDatasetIndex].data[curIndex];

		if (type === 'radar' || type === 'polarArea') {
			dataPoint = calcRadar(event, chartInstance, element);
		} else if (stacked) {
			const cursorPos = calcPosition(event, chartInstance, curDatasetIndex, curIndex, dataPoint);
			dataPoint = roundValue(cursorPos - initValue, pluginOptions.round);
		} else if (floatingBar) {
			dataPoint = calcPosition(event, chartInstance, curDatasetIndex, curIndex, dataPoint);
		} else {
			dataPoint = calcPosition(event, chartInstance, curDatasetIndex, curIndex, dataPoint);
		}

		if (!callback || (typeof callback === 'function' && callback(event, curDatasetIndex, curIndex, dataPoint) !== false)) {
			chartInstance.data.datasets[curDatasetIndex].data[curIndex] = dataPoint;
			chartInstance.update('none');
		}
	}
}

function dragEndCallback (e, chartInstance, callback) {
	// curDatasetIndex
	curIndex = undefined;
	isDragging = false;
	// re-enable the tooltip animation
	if (chartInstance.config.options.plugins.tooltip) {
		chartInstance.config.options.plugins.tooltip.animation = eventSettings;
		chartInstance.update('none');
	}

	// chartInstance.update('none')
	if (typeof callback === 'function' && element) {
		const datasetIndex = element.datasetIndex;
		const index = element.index;
		const value = applyMagnet(chartInstance, datasetIndex, index);
		element = null;
		return callback(e, datasetIndex, index, value);
	}

	// noEventDefault(e)
}

function getSafe (func) {
	try {
		return func();
	} catch (e) {
		return '';
	}
}

function determineElement (e, chartInstance) {
	const { x, y } = getEventXAndY(e, chartInstance, true);

	const datasetMeta = chartInstance.getDatasetMeta(0);
	const pointerElements = datasetMeta.data;
	rAxisID = datasetMeta.rAxisID;
	const rScale = chartInstance.scales[rAxisID];
	const numPoints = pointerElements.length;

	const pi = Math.PI;
	const oneAngle = 2 * pi / numPoints;

	const mousePointerCenterAdjustedX = x - rScale.xCenter;
	const mousePointerCenterAdjustedY = -(y - rScale.yCenter);

	let minD = Number.POSITIVE_INFINITY;
	let lineIndex;

	for (let i = 0; i < numPoints; i++) {
		const angle = pi / 2 - oneAngle * i;
		const anglePi = Math.round(angle, 4) === -Math.round(pi, 4);
		const angleZero = Math.round(angle, 4) === 0;
		const isSlopeZero = angleZero || anglePi;
		const isInUpperHalfCircle = angle <= -pi || (angle <= pi / 2 && angle > 0);

		const correctSideY = ((isInUpperHalfCircle && mousePointerCenterAdjustedY > 0)
      || (!isInUpperHalfCircle && mousePointerCenterAdjustedY < 0)) && !isSlopeZero;
		const ifZeroCorrectSideX = (angleZero && mousePointerCenterAdjustedX > 0)
      || (anglePi && mousePointerCenterAdjustedX < 0);

		if (correctSideY || ifZeroCorrectSideX) {
			const slope = Math.tan(angle);
			const absSlope = Math.abs(slope);

			let tempIntersectionX, tempIntersectionY;
			if (absSlope < 0.0001) /* an arbitrary low number */ {
				tempIntersectionX = mousePointerCenterAdjustedX;
				tempIntersectionY = 0;
			} else if (absSlope > 10000 /* an arbitrary large number for when tan(x) is undefined */) {
				tempIntersectionX = 0;
				tempIntersectionY = mousePointerCenterAdjustedY;
			} else {
				const mPointerLine = mousePointerCenterAdjustedY + 1 / slope * mousePointerCenterAdjustedX;

				tempIntersectionX = mPointerLine / (slope + 1 / slope);
				tempIntersectionY = slope * tempIntersectionX;
			}

			const d = (tempIntersectionX - mousePointerCenterAdjustedX) ** 2 + (tempIntersectionY - mousePointerCenterAdjustedY) ** 2;

			if (d < minD) {
				minD = d;
				lineIndex = i;
			}
		}
	}

	return { element: pointerElements[lineIndex], index: lineIndex };
}

function openToolTip (chart, pointIndex) {
	const tooltip = chart.tooltip;
	if (tooltip.getActiveElements().length > 1) {
		tooltip.setActiveElements([]);
	} else {
		tooltip.setActiveElements([
			{
				datasetIndex: 0,
				index: pointIndex
			}
		]);
	}
}

function roundValue (value, pos) {
	if (!isNaN(pos)) {
		return Math.round(value * 10 ** pos) / 10 ** pos;
	}
	return value;
}

function calcRadar (e, chartInstance, element) {
	let v;
	const { x, y } = getEventXAndY(e, chartInstance, true);

	const axisAngle = element.element.angle;
	const degrees = axisAngle * (180 / Math.PI);
	const slope = -Math.round(Math.tan(axisAngle));

	const rScale = chartInstance.scales[rAxisID];

	const mousePointerCenterAdjustedX = x - rScale.xCenter;
	const mousePointerCenterAdjustedY = -(y - rScale.yCenter);

	let intersectionX, intersectionY;
	if (slope === 0) {
		intersectionX = mousePointerCenterAdjustedX;
		intersectionY = 0;
	} else if (degrees === 90 || degrees === -90) {
		intersectionX = 0;
		intersectionY = mousePointerCenterAdjustedY;
	} else {
		const mPointerLine = mousePointerCenterAdjustedY + 1 / slope * mousePointerCenterAdjustedX;

		intersectionX = mPointerLine / (slope + 1 / slope);
		intersectionY = slope * intersectionX;
	}

	const d = Math.sqrt((intersectionX) ** 2 + (intersectionY) ** 2);
	const scalingFactor = rScale.drawingArea / (rScale.max - rScale.min);
	if (rScale.options.ticks.reverse) {
		v = rScale.max - (d / scalingFactor);
	} else {
		v = rScale.min + (d / scalingFactor);
	}

	v = roundValue(v, chartInstance.config.options.plugins.dragData.round);

	v = v > chartInstance.scales[rAxisID].max ? chartInstance.scales[rAxisID].max : v;
	v = v < chartInstance.scales[rAxisID].min ? chartInstance.scales[rAxisID].min : v;

	if ((degrees === -90 && intersectionY < 0)
    || (degrees === 90 && intersectionY > 0)
    || (degrees > -90 && degrees < 90 && intersectionX < 0)
    || (degrees > 90 && degrees < 270 && intersectionX > 0)
	) {
		v = 0;
	}

	return v;
}

function calcPosition (e, chartInstance, datasetIndex, index) {
	let { x, y } = getEventXAndY(e, chartInstance, true);
	const dataPoint = chartInstance.data.datasets[datasetIndex].data[index];

	x = chartInstance.scales[xAxisID].getValueForPixel(x);
	y = chartInstance.scales[yAxisID].getValueForPixel(y);

	x = roundValue(x, chartInstance.config.options.plugins.dragData.round);
	y = roundValue(y, chartInstance.config.options.plugins.dragData.round);

	x = x > chartInstance.scales[xAxisID].max ? chartInstance.scales[xAxisID].max : x;
	x = x < chartInstance.scales[xAxisID].min ? chartInstance.scales[xAxisID].min : x;

	y = y > chartInstance.scales[yAxisID].max ? chartInstance.scales[yAxisID].max : y;
	y = y < chartInstance.scales[yAxisID].min ? chartInstance.scales[yAxisID].min : y;

	if (floatingBar) {
		// x contains the new value for one end of the floating bar
		// dataPoint contains the old interval [left, right] of the floating bar
		// calculate difference between the new value and both sides
		// the side with the smallest difference from the new value was the one that was dragged
		// return an interval with new value on the dragged side and old value on the other side
		let newVal;
		// choose the right variable based on the orientation of the graph(vertical, horizontal)
		if (chartInstance.config.options.indexAxis === 'y') {
			newVal = x;
		} else {
			newVal = y;
		}
		const diffFromLeft = Math.abs(newVal - dataPoint[0]);
		const diffFromRight = Math.abs(newVal - dataPoint[1]);

		if (diffFromLeft <= diffFromRight) {
			return [newVal, dataPoint[1]];
		} else {
			return [dataPoint[0], newVal];
		}
	}

	if (dataPoint.x !== undefined && chartInstance.config.options.plugins.dragData.dragX) {
		dataPoint.x = x;
	}

	if (dataPoint.y !== undefined) {
		if (chartInstance.config.options.plugins.dragData.dragY !== false) {
			dataPoint.y = y;
		}
		return dataPoint;
	} else if (chartInstance.config.options.indexAxis === 'y') {
		return x;
	} else {
		return y;
	}
}

// Update values to the nearest values
function applyMagnet (chartInstance, i, j) {
	const pluginOptions = chartInstance.config.options.plugins.dragData;
	if (pluginOptions.magnet) {
		const magnet = pluginOptions.magnet;
		if (magnet.to && typeof magnet.to === 'function') {
			let data = chartInstance.data.datasets[i].data[j];
			data = magnet.to(data);
			chartInstance.data.datasets[i].data[j] = data;
			chartInstance.update('none');
			return data;
		}
	} else {
		return chartInstance.data.datasets[i].data[j];
	}
}

const ChartJSdragDataPlugin = {
	id: 'dragdata',
	afterInit (chartInstance) {
		if (chartInstance.config.options.plugins && chartInstance.config.options.plugins.dragData) {
			const pluginOptions = chartInstance.config.options.plugins.dragData;

			bindEvents(chartInstance, pluginOptions);
		}
	},
	beforeEvent (chart) {
		if (isDragging) {
			if (chart.tooltip) {
				chart.tooltip.update();
			}
			return false;
		}
	}
};

function getEventXAndY (event, chartInstance) {
	let x, y;
	if (event.touches) {
		x = event.touches[0].clientX - chartInstance.canvas.getBoundingClientRect().left;
		y = event.touches[0].clientY - chartInstance.canvas.getBoundingClientRect().top;
	} else {
		x = event.clientX - chartInstance.canvas.getBoundingClientRect().left;
		y = event.clientY - chartInstance.canvas.getBoundingClientRect().top;
	}
	return { x, y };
}

function isClickInsideChart (event, chartInstance) {
	if (type !== 'radar') {
		return true;
	}
	// Calcluate the radius of the chart based on the labels with the minX and max X of the chart
	const labels = chartInstance.scales.r._pointLabelItems;
	// Find the label with the lowest x value
	const minXLabel = labels.find(label => label.x === labels.reduce((min, label) => label.x < min ? label.x : min, Number.POSITIVE_INFINITY));
	// Find the label with the highest x value
	const maxXLabel = labels.find(label => label.x === labels.reduce((max, label) => label.x > max ? label.x : max, Number.NEGATIVE_INFINITY));
	const minX = minXLabel.right;
	const maxY = maxXLabel.left;
	// Calculate the distance between the mouse pointer and the center of the chart
	const { x, y } = getEventXAndY(event, chartInstance);
	const { xCenter, yCenter } = chartInstance.scales.r;
	const mousePointerCenterAdjustedX = x - xCenter;
	const mousePointerCenterAdjustedY = -(y - yCenter);
	const radius = (maxY - minX) / 2 + 20;
	const distance = Math.sqrt(mousePointerCenterAdjustedX ** 2 + mousePointerCenterAdjustedY ** 2);
	// Check if the distance is less than the radius of the chart
	return (distance <= radius && !event.ctrlKey && !event.button);
}

function bindEvents (chartInstance, pluginOptions) {
	['mousedown', 'touchstart'].forEach((eventName) => {
		chartInstance.canvas.addEventListener(eventName, (event) => {
			getElement(event, chartInstance, pluginOptions.onDragStart);
		});
	});

	['mousemove', 'touchmove'].forEach((eventName) => {
		chartInstance.canvas.addEventListener(eventName, (event) => {
			updateData(event, chartInstance, pluginOptions.onDrag);
		}, { capture: true, passive: false });
	});

	['mouseup', 'touchend'].forEach((eventName) => {
		chartInstance.canvas.addEventListener(eventName, (event) => {
			dragEndCallback(event, chartInstance, pluginOptions.onDragEnd);
		});
	});
}

function noEventDefault (event) {
	event.preventDefault();
	event.stopImmediatePropagation();
}


// import InnerLabelPlugin from '~/utils/chartJs.plugin.radarLabels'
ChartJS.register(RadialLinearScale, PointElement, LineElement, Filler, Tooltip, ChartJSdragDataPlugin);
export default {
	components: {
		Radar
	},
	props: {
		isSsr: {
			type: Boolean,
			default: false
		},
		isMutable: {
			type: Boolean,
			default: false
		},
		draggableIndexes: {
			type: [Array],
			default: () => []
		},
		chartData: {
			type: [Object],
			default: () => ({}),
			required: true,
			validator: (value) => {
				return value.labels && value.datasets;
			}
		},
		options: {
			type: [Object],
			default: () => ({})
		}
	},
	emits: ['update:chartData', 'onDragStart', 'onDrag', 'onDragEnd'],
	data () {
		return {
			internalData: JSON.parse(JSON.stringify(this.chartData))
		};
	},
	computed: {
		internalOptions () {
			return {
				// It's possible to pass a click handler, note: click, ergo not mousedown
				// onClick: this.onClick,
				...this.options,
				plugins: {
					...insertIf(this.isMutable, {
						dragData: {
							draggableIndexes: this.draggableIndexes,
							round: 0,
							onDragStart: this.onDragStart,
							onDrag: this.onDrag,
							onDragEnd: this.onDragEnd
						}
					}),
					...insertIf(this.options?.plugins, this.options.plugins)
				}
			};
		}
	},
	watch: {
		chartData: {
			handler (value) {
				this.internalData = JSON.parse(JSON.stringify(value));
			},
			deep: true
		},
		options: {
			handler (value) {
				this.internalOptions = value;
			},
			deep: true
		}
	},
	methods: {
		onDragStart (event, element, index, value) {
			if (this.draggableIndexes.length && !this.draggableIndexes.includes(element)) {
				return false;
			}
			this.$emit('onDragStart', { event, element, index, value });
		},
		onDrag (event, datasetIndex, index, value) {
			this.$emit('onDrag', { event, datasetIndex, index, value });
		},
		onDragEnd (event, datasetIndex, index, value) {
			this.$nextTick(() => {
				this.$emit('onDragEnd', this.internalData);
			});
		}
	}
};
</script>

<style>
.baseChartRadar:not(.isMutable) {
  pointer-events: none;
}
</style>
